<script setup lang="ts">
import { ref, computed } from 'vue'
import { SunIcon, MoonIcon, Bars3CenterLeftIcon } from '@heroicons/vue/24/solid'
import type { ILink } from '@/misc/types'
import { FwbNavbar, FwbNavbarLogo, FwbNavbarCollapse, FwbNavbarLink } from 'flowbite-vue'
import logoUrlLight from '@/assets/norsonic-logo-full-color-rgb.png'
import logoUrlDark from '@/assets/norsonic-logo-reverse-rgb.png'
import { useSharedStore } from '@/stores/shared'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'

defineProps({
  links: {
    type: Array as () => ILink[],
    default: () => []
  }
})

const prefersDark =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches

const isDark = ref(
  localStorage.getItem('color-theme') === 'dark' ||
    (!('color-theme' in localStorage) && prefersDark)
)

const toggleTheme = () => {
  isDark.value = !isDark.value
  // if set via local storage previously
  if (localStorage.getItem('color-theme')) {
    if (localStorage.getItem('color-theme') === 'light') {
      document.documentElement.classList.add('dark')
      localStorage.setItem('color-theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('color-theme', 'light')
    }

    // if NOT set via local storage previously
  } else {
    if (document.documentElement.classList.contains('dark')) {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('color-theme', 'light')
    } else {
      document.documentElement.classList.add('dark')
      localStorage.setItem('color-theme', 'dark')
    }
  }
}

const router = useRouter()

const hideSidebar = computed(() => router.currentRoute.value.name === 'home')

const sharedStore = useSharedStore()
const { showSidebar } = storeToRefs(sharedStore)
</script>

<template>
  <FwbNavbar solid class="fixed w-full z-30 bg-white dark:bg-[#0a1929] shadow">
    <template #logo>
      <button
        @click="showSidebar = true"
        id="toggleSidebarMobile"
        class="p-2 text-brand-dark rounded cursor-pointer lg:hidden hover:text-brand hover:bg-brand-neutral-100 focus:bg-brand-neutral-100 focus:ring-2 focus:ring-brand-neutral-200 dark:focus:bg-brand/30 dark:hover:bg-brand/20 dark:focus:ring-brand/40 dark:text-white"
      >
        <Bars3CenterLeftIcon class="w-6 h-6" v-if="!hideSidebar" />
      </button>
      <router-link :to="'/'">
        <FwbNavbarLogo
          link="#"
          alt="NoiseTag logo"
          :image-url="isDark ? logoUrlDark : logoUrlLight"
        ></FwbNavbarLogo
      ></router-link>
    </template>
    <template #default="{ isShowMenu }">
      <FwbNavbarCollapse :isShowMenu="isShowMenu">
        <router-link
          v-for="link in links"
          :key="link.url"
          :to="{ name: link.url }"
          v-slot="{ isActive }"
        >
          <FwbNavbarLink
            :is-active="isActive"
            link=""
            class="text-brand-dark hover:text-brand dark:text-brand-neutral-200 dark:hover:text-white"
          >
            {{ link.text }}
          </FwbNavbarLink>
        </router-link>
        <button
          @click="toggleTheme"
          class="ml-2 text-brand-dark dark:text-brand-neutral-200 hover:bg-brand-neutral-100 dark:hover:bg-brand/20 focus:outline-none dark:focus:ring-brand rounded-lg text-sm"
        >
          <SunIcon v-if="isDark" class="w-6 h-6" />
          <MoonIcon v-else class="w-6 h-6" />
        </button>
      </FwbNavbarCollapse>
    </template>
  </FwbNavbar>
</template>
